/**
 * RecipeBookModal Component
 *
 * Modal interface for managing substrate recipes with version control.
 * Displays recipes in tabs: Main Recipes, Experimental, and All.
 *
 * Features:
 * - Tab-based filtering (main vs experimental recipes)
 * - Table view with recipe details and actions
 * - Add/Edit/Delete operations via nested RecipeForm dialog
 * - Promote experimental recipes to main versions
 * - View version history
 *
 * Part of Recipe Book feature implementation
 */

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useRecipes, useRecipeMutations } from '@/hooks/useSubstrateResources'
import { useRecipesWithCost } from '@/hooks/useRecipeCost'
import RecipeForm from './RecipeForm'
import {
  BookOpen,
  Plus,
  Beaker,
  FlaskConical,
  Pencil,
  Trash2,
  ArrowUp,
  History,
  AlertTriangle,
  Loader2,
  MoreHorizontal
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'

/**
 * Format a number as GBP currency
 */
function formatCurrency(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '—'
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * Helper to determine if a recipe is experimental
 */
function isExperimental(recipe) {
  return recipe.version_minor > 0
}

/**
 * Helper to format version display
 */
function formatVersion(recipe) {
  if (!recipe.version_major) return 'v1'
  return recipe.version_minor > 0
    ? `v${recipe.version_major}.${recipe.version_minor}`
    : `v${recipe.version_major}`
}

/**
 * RecipeBookModal Main Component
 */
export default function RecipeBookModal({ open, onOpenChange }) {
  const [activeTab, setActiveTab] = useState('main')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingRecipe, setEditingRecipe] = useState(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [promoteConfirmId, setPromoteConfirmId] = useState(null)

  // Fetch recipes (including inactive for comprehensive view)
  const { recipes, loading, error, refetch } = useRecipes({ activeOnly: false })

  // Fetch recipes with cost data
  const {
    recipes: recipesWithCost,
    isLoading: costLoading,
    refetch: refetchCosts
  } = useRecipesWithCost({
    includeCost: true,
    activeOnly: false,
    currentOnly: false
  })

  const { deleteRecipe, loading: mutationLoading } = useRecipeMutations()

  // Build cost lookup by recipe ID
  const costByRecipeId = {}
  for (const recipe of recipesWithCost) {
    if (recipe.cost) {
      costByRecipeId[recipe.id] = recipe.cost
    }
  }

  // Filter recipes by type
  const mainRecipes = recipes.filter(r => !isExperimental(r))
  const experimentalRecipes = recipes.filter(r => isExperimental(r))

  // Handlers
  const handleAddRecipe = () => {
    setEditingRecipe(null)
    setIsFormOpen(true)
  }

  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe)
    setIsFormOpen(true)
  }

  const handleDeleteRecipe = async () => {
    if (!deleteConfirmId) return

    try {
      await deleteRecipe(deleteConfirmId)
      setDeleteConfirmId(null)
      refetch()
      refetchCosts()
    } catch (err) {
      console.error('Failed to delete recipe:', err)
    }
  }

  const handlePromoteRecipe = async () => {
    if (!promoteConfirmId) return

    try {
      // TODO: Implement promotion logic
      // This will be implemented in Step 5 of the implementation plan
      console.log('Promote recipe:', promoteConfirmId)
      setPromoteConfirmId(null)
      refetch()
      refetchCosts()
    } catch (err) {
      console.error('Failed to promote recipe:', err)
    }
  }

  const handleViewHistory = (recipeId) => {
    // TODO: Implement version history view
    // This will be implemented in Step 7 of the implementation plan
    console.log('View history for recipe:', recipeId)
  }

  const handleFormSave = () => {
    setIsFormOpen(false)
    refetch()
    refetchCosts()
  }

  // Render species badges (truncated if too many)
  const renderSpecies = (species) => {
    if (!species?.length) {
      return <span className="text-content-tertiary">All species</span>
    }
    const maxShow = 3
    const shown = species.slice(0, maxShow)
    const remaining = species.length - maxShow

    return (
      <div className="flex flex-wrap gap-1">
        {shown.map(s => (
          <Badge key={s} variant="outline" className="text-xs">
            {s}
          </Badge>
        ))}
        {remaining > 0 && (
          <Badge variant="secondary" className="text-xs">
            +{remaining}
          </Badge>
        )}
      </div>
    )
  }

  // Render table for a recipe list
  const renderRecipeTable = (recipeList, emptyMessage) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-content-tertiary" />
        </div>
      )
    }

    if (recipeList.length === 0) {
      return (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-content-tertiary mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{emptyMessage}</h3>
          <Button variant="outline" onClick={handleAddRecipe}>
            <Plus className="h-4 w-4 mr-2" />
            Add Recipe
          </Button>
        </div>
      )
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Recipe Name</TableHead>
            <TableHead>Species</TableHead>
            <TableHead className="text-right">Cost/Batch</TableHead>
            <TableHead className="text-right">Ingredients</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recipeList.map(recipe => {
            const experimental = isExperimental(recipe)
            const costData = costByRecipeId[recipe.id]
            const hasStockIssue = costData && !costData.all_ingredients_available

            return (
              <TableRow key={recipe.id}>
                {/* Recipe Name with badges */}
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{recipe.name}</span>
                    <Badge variant={experimental ? 'outline' : 'default'} className="text-xs">
                      {formatVersion(recipe)}
                    </Badge>
                    {experimental && (
                      <Badge variant="secondary" className="text-xs bg-odin-purple/10 text-odin-purple border-odin-purple/20">
                        <Beaker className="h-3 w-3 mr-1" />
                        Experimental
                      </Badge>
                    )}
                    {recipe.is_current && (
                      <Badge variant="outline" className="text-xs border-odin-green text-odin-green">
                        Current
                      </Badge>
                    )}
                  </div>
                </TableCell>

                {/* Species */}
                <TableCell>
                  {renderSpecies(recipe.compatible_species)}
                </TableCell>

                {/* Cost/Batch */}
                <TableCell className="text-right">
                  {costLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin ml-auto" />
                  ) : costData ? (
                    <div className="flex items-center justify-end gap-1">
                      {hasStockIssue && (
                        <AlertTriangle
                          className="h-4 w-4 text-odin-amber"
                          title="Some ingredients have insufficient stock"
                        />
                      )}
                      <span className={hasStockIssue ? 'text-odin-amber' : ''}>
                        {formatCurrency(costData.total_cost_per_batch)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-content-tertiary">—</span>
                  )}
                </TableCell>

                {/* Ingredients count */}
                <TableCell className="text-right">
                  {recipe.ingredients?.length || 0}
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" disabled={mutationLoading}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditRecipe(recipe)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit Recipe
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewHistory(recipe.id)}>
                        <History className="h-4 w-4 mr-2" />
                        View History
                      </DropdownMenuItem>
                      {experimental && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setPromoteConfirmId(recipe.id)}
                            className="text-odin-green"
                          >
                            <ArrowUp className="h-4 w-4 mr-2" />
                            Promote to Main
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-odin-red"
                        onClick={() => setDeleteConfirmId(recipe.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Recipe
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Recipe Book
            </DialogTitle>
          </DialogHeader>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Error loading recipes: {error}</AlertDescription>
            </Alert>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="main" className="flex items-center gap-2">
                  <FlaskConical className="h-4 w-4" />
                  Main Recipes
                  {!loading && (
                    <Badge variant="secondary" className="ml-1">
                      {mainRecipes.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="experimental" className="flex items-center gap-2">
                  <Beaker className="h-4 w-4" />
                  Experimental
                  {!loading && (
                    <Badge variant="secondary" className="ml-1">
                      {experimentalRecipes.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="all" className="flex items-center gap-2">
                  All Recipes
                  {!loading && (
                    <Badge variant="secondary" className="ml-1">
                      {recipes.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <Button onClick={handleAddRecipe}>
                <Plus className="h-4 w-4 mr-2" />
                Add Recipe
              </Button>
            </div>

            {/* Tab Contents */}
            <div className="flex-1 overflow-y-auto">
              <TabsContent value="main" className="mt-0">
                {renderRecipeTable(mainRecipes, 'No main recipes')}
              </TabsContent>

              <TabsContent value="experimental" className="mt-0">
                {renderRecipeTable(experimentalRecipes, 'No experimental recipes')}
              </TabsContent>

              <TabsContent value="all" className="mt-0">
                {renderRecipeTable(recipes, 'No recipes yet')}
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Recipe Form Dialog (nested) */}
      {isFormOpen && (
        <RecipeForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSave={handleFormSave}
          editingRecipe={editingRecipe}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently deactivate this recipe. It will no longer be available
              for new substrate batches. Existing batches using this recipe will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRecipe}
              className="bg-odin-red hover:bg-odin-red/90"
            >
              Delete Recipe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Promote Confirmation Dialog */}
      <AlertDialog open={!!promoteConfirmId} onOpenChange={() => setPromoteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote to Main Recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new main version of this recipe, incrementing the major version number.
              The current main version will be marked as superseded.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePromoteRecipe}
              className="bg-odin-green hover:bg-odin-green/90"
            >
              Promote Recipe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
